import type { EnrichCustomerResponse } from '@strata-monorepo/shared-types'
import sanitizeData from './sanitizeData'
import { ENRICH_CUSTOMER_SENSITIVE_FIELDS } from './constants/piiSensitiveData'

/**
 * One-time hand-off from Strata's plan-details address block to FMP's home details step: the
 * picked address and whatever the lookup found. FMP reads it once when its store is created.
 * sessionStorage carries it because FMP always lives on the grid's own site.
 */
export const FMP_HOME_INFO_HANDOFF_KEY = 'fmpHomeInfoHandoff'

/**
 * The picked address, shaped like shared-ui's `Address`. Declared here rather than imported:
 * shared-ui already depends on this library, so importing back would be circular.
 */
export interface FmpHomeInfoHandoffAddress {
  street_line: string
  secondary?: string
  city: string
  state: string
  zipcode: string
}

export interface FmpHomeInfoHandoff {
  address: FmpHomeInfoHandoffAddress
  enrichCustomerResponse: EnrichCustomerResponse | null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const readHandoff = (raw: string | null): FmpHomeInfoHandoff | null => {
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed) || !isRecord(parsed.address)) return null
    if (typeof parsed.address.zipcode !== 'string' || !parsed.address.zipcode) return null

    return {
      address: parsed.address as unknown as FmpHomeInfoHandoffAddress,
      enrichCustomerResponse: isRecord(parsed.enrichCustomerResponse)
        ? (parsed.enrichCustomerResponse as unknown as EnrichCustomerResponse)
        : null,
    }
  } catch {
    return null
  }
}

/** Saves the hand-off and reads it back; false when sessionStorage is blocked or full. */
export function saveFmpHomeInfoHandoff({
  address,
  enrichCustomerResponse,
}: FmpHomeInfoHandoff): boolean {
  const handoff: FmpHomeInfoHandoff = {
    address,
    // The scrubber's dot-path half writes into the object it's given, so it gets a copy
    enrichCustomerResponse: enrichCustomerResponse
      ? sanitizeData(structuredClone(enrichCustomerResponse), ENRICH_CUSTOMER_SENSITIVE_FIELDS)
      : null,
  }
  const serialized = JSON.stringify(handoff)

  try {
    window.sessionStorage.setItem(FMP_HOME_INFO_HANDOFF_KEY, serialized)
    return window.sessionStorage.getItem(FMP_HOME_INFO_HANDOFF_KEY) === serialized
  } catch {
    return false
  }
}

/** Reads the hand-off once and removes it, so a later FMP visit in the tab starts clean. */
export function takeFmpHomeInfoHandoff(): FmpHomeInfoHandoff | null {
  try {
    const raw = window.sessionStorage.getItem(FMP_HOME_INFO_HANDOFF_KEY)
    if (raw !== null) window.sessionStorage.removeItem(FMP_HOME_INFO_HANDOFF_KEY)
    return readHandoff(raw)
  } catch {
    return null
  }
}
